import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Lock,
  ShieldAlert,
  ShieldCheck,
  Shield,
  KeyRound,
  User,
  ArrowRight,
  AlertTriangle,
  RotateCcw,
  Info,
  Key,
} from 'lucide-react';

export default function LoginPage() {
  const { loginUser, showToast, settings, customPathSlug, navigate, user } = useApp();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Brute-force status
  const [isLocked, setIsLocked] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [remainingAttempts, setRemainingAttempts] = useState<number>(25);

  const primaryColor = settings?.theme.primaryColor || '#059669';
  const configuredSecret = (settings?.security?.customLoginPath || '').replace(/^\/+|\/+$/g, '');

  // If a secret login path is active and visitor is not accessing through it, redirect to home
  useEffect(() => {
    if (configuredSecret && !user) {
      const current = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase().trim();
      if (current !== configuredSecret.toLowerCase().trim()) {
        navigate('home');
      }
    }
  }, [configuredSecret, user, navigate]);

  // Customizable Login Page Configuration
  const loginCfg = settings?.loginPageConfig || {
    badgeText: 'Portal Khusus Tim & Redaksi',
    title: 'Login Dasbor Redaksi',
    subtitle: 'Masuk untuk mengelola artikel, marketplace UMKM, portofolio, dan tema website',
    usernameLabel: 'Username / ID Pengguna',
    usernamePlaceholder: 'Masukkan username Anda',
    passwordLabel: 'Kata Sandi (Password)',
    passwordPlaceholder: '••••••••',
    submitButtonText: 'Masuk ke Dasbor',
    backHomeText: '← Kembali ke Halaman Depan',
    footerNotice: 'Sistem terenkripsi dengan proteksi Anti Brute-Force aktif',
    showSecretPathHint: true,
    customIcon: 'lock',
  };

  // Icon Selector
  const renderIcon = () => {
    if (isLocked) return <ShieldAlert className="w-7 h-7" />;
    switch (loginCfg.customIcon) {
      case 'shield':
        return <Shield className="w-7 h-7" />;
      case 'key':
        return <Key className="w-7 h-7" />;
      case 'user':
        return <User className="w-7 h-7" />;
      case 'lock':
      default:
        return <Lock className="w-7 h-7" />;
    }
  };

  // Check rate limit status on mount
  useEffect(() => {
    async function checkStatus() {
      try {
        const res = await fetch('/api/auth/check-status', { method: 'POST' });
        const data = await res.json();
        if (data.locked) {
          setIsLocked(true);
          setRemainingSeconds(data.remainingSeconds);
        } else {
          setIsLocked(false);
          setRemainingAttempts(data.remainingAttempts);
        }
      } catch (err) {
        console.error(err);
      }
    }
    checkStatus();
  }, []);

  // Countdown timer when locked
  useEffect(() => {
    if (!isLocked || remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          setIsLocked(false);
          setRemainingAttempts(25);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isLocked, remainingSeconds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setErrorMessage('Silakan isi username dan password Anda.');
      return;
    }

    if (isLocked) {
      showToast('Akses login masih terkunci. Silakan tunggu timer selesai.', 'error');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          secretPathAttempt: customPathSlug || window.location.pathname.replace(/^\/+|\/+$/g, ''),
        }),
      });

      const data = await res.json();

      if (data.success && data.user && data.token) {
        loginUser(data.user, data.token);
      } else {
        if (data.locked) {
          setIsLocked(true);
          setRemainingSeconds(data.remainingSeconds);
        } else if (data.remainingAttempts !== undefined) {
          setRemainingAttempts(data.remainingAttempts);
        }
        setErrorMessage(data.message || 'Login gagal. Periksa username dan password.');
        showToast(data.message || 'Login gagal', 'error');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Terjadi kegagalan jaringan saat otentikasi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetRateLimit = async () => {
    try {
      await fetch('/api/auth/reset-ratelimit', { method: 'POST' });
      setIsLocked(false);
      setRemainingSeconds(0);
      setRemainingAttempts(25);
      setErrorMessage(null);
      showToast('Status Rate-limit berhasil di-reset!', 'info');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div id="login-page-view" className="min-h-[82vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-7 bg-white p-8 sm:p-10 rounded-3xl border border-slate-200 shadow-xl">
        {/* Brand & Title */}
        <div className="text-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white mx-auto shadow-md mb-4 transition-transform hover:scale-105"
            style={{ backgroundColor: isLocked ? '#E11D48' : primaryColor }}
          >
            {renderIcon()}
          </div>

          {loginCfg.badgeText && (
            <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-[11px] font-bold rounded-full mb-2">
              {loginCfg.badgeText}
            </span>
          )}

          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {isLocked ? 'Akses Login Dikunci' : (loginCfg.title || 'Login Dasbor Redaksi')}
          </h2>
          {loginCfg.subtitle && (
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              {loginCfg.subtitle}
            </p>
          )}
        </div>

        {/* Secret Login Path Notice if configured & enabled in settings */}
        {configuredSecret && loginCfg.showSecretPathHint !== false && (
          <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-bold">Gerbang URL Khusus Aktif:</span>
              <p className="text-[11px] text-amber-700 mt-0.5">
                Jalur rahasia: <code className="font-mono bg-white px-1 py-0.5 rounded-sm">/{configuredSecret}</code>
              </p>
            </div>
          </div>
        )}

        {/* Lockout Banner */}
        {isLocked && (
          <div className="p-4 bg-rose-50 rounded-2xl border border-rose-200 text-rose-800 text-xs space-y-2">
            <div className="flex items-center gap-2 font-bold text-rose-900">
              <AlertTriangle className="w-4 h-4" />
              <span>Proteksi Anti Brute-Force Aktif</span>
            </div>
            <p className="text-rose-700 leading-relaxed">
              Anda telah gagal login beberapa kali. Akses IP dikunci sementara demi keamanan database (30 detik).
            </p>
            <div className="font-mono text-sm font-extrabold text-rose-900 pt-1">
              Sisa waktu buka: {remainingSeconds} detik
            </div>
            <button
              onClick={handleResetRateLimit}
              className="mt-2 w-full py-1.5 bg-rose-700 text-white rounded-lg font-bold text-[11px] flex items-center justify-center gap-1 hover:bg-rose-800"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Buka Kunci Sekarang (Reset Rate-Limit)</span>
            </button>
          </div>
        )}

        {/* Attempts remaining indicator */}
        {!isLocked && remainingAttempts < 25 && (
          <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-800 flex items-center justify-between">
            <span>Sisa percobaan sebelum dikunci:</span>
            <span className="font-mono font-extrabold px-2 py-0.5 bg-amber-200 rounded-md">
              {remainingAttempts} / 25
            </span>
          </div>
        )}

        {/* Error message */}
        {errorMessage && !isLocked && (
          <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 text-xs text-rose-700 font-medium leading-relaxed">
            {errorMessage}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">
              {loginCfg.usernameLabel || 'Username / ID Pengguna'}
            </label>
            <div className="relative">
              <input
                type="text"
                required
                disabled={isLocked}
                placeholder={loginCfg.usernamePlaceholder || 'Masukkan username Anda'}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800 disabled:opacity-50"
              />
              <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">
              {loginCfg.passwordLabel || 'Kata Sandi (Password)'}
            </label>
            <div className="relative">
              <input
                type="password"
                required
                disabled={isLocked}
                placeholder={loginCfg.passwordPlaceholder || '••••••••'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800 disabled:opacity-50"
              />
              <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || isLocked}
            className="w-full py-3.5 rounded-xl text-white font-bold text-xs shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            style={{ backgroundColor: primaryColor }}
          >
            <span>{submitting ? 'Memverifikasi...' : (loginCfg.submitButtonText || 'Masuk ke Dasbor')}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        {/* Footer Notice */}
        {loginCfg.footerNotice && (
          <p className="text-[11px] text-center text-slate-400 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>{loginCfg.footerNotice}</span>
          </p>
        )}

        {/* Back to Home */}
        <div className="text-center pt-1 border-t border-slate-100">
          <button
            onClick={() => navigate('home')}
            className="text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
          >
            {loginCfg.backHomeText || '← Kembali ke Halaman Depan'}
          </button>
        </div>
      </div>
    </div>
  );
}
